import { useEffect, useId, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CampoDeMoeda,
  Aviso,
  Botao,
  Campo,
  CampoDeSelecao,
  CartaoIndicador,
  EstadoVazio,
  MenuDeAcoes,
  Modal,
  Selo,
  Tabela,
  ValorFiscal,
  ValorPercentual,
  useDialogo,
  type ColunaTabela,
  type TomSemantico,
} from '../componentes/ui';
import { AvisoDoLote, BotaoDoLote, useAcaoEmLote } from '../componentes/AcaoEmLote';
import { BotaoExtrairXlsx } from '../componentes/BotaoExtrairXlsx';
import { PedidosDaOperacao, invalidarPedidos } from '../componentes/PedidosDeFechamento';
import { atenderPedido, type PedidoDeFechamento } from '../servicos/participacao';
import { requisicaoAutenticada } from '../servicos/http';
import { resumirNomes } from '../servicos/lote';
import { useCarteira, useClientesParaEscolher } from '../hooks/useOperacao';
import { simOuNao, type ColunaDaPlanilha } from '../servicos/planilha';
import { formatarCnpj, lerValorDigitado, moedaParaCampo } from '../servicos/formato';
import { Pagina } from './Carteira';
import { ErroDaApi } from './ErroDaApi';
import { ParcelasDoContrato } from './PassivoParcelas';
import { explicacaoDoSistema } from './passivo/sistema-de-amortizacao';
import {
  CampoDaFormaDePagamento,
  formaDePagamentoValida,
  useLeituraDaFormaDePagamento,
} from './passivo/FormaDePagamento';

export interface ContratoPassivo {
  readonly id: string;
  readonly clienteId: string;
  readonly fornecedorId: string;
  readonly status: 'SIMULACAO' | 'ATIVO' | 'FINALIZADO' | 'CANCELADO';
  readonly valorDividaOriginal: number;
  readonly valorFinalPagar: number;
  readonly economia: number;
  readonly percentualEconomia: number;
  readonly quantidadeParcelas: number;
  readonly cronogramaGerado: boolean;
  readonly checklistIniciado: boolean;
  /** A forma de pagamento como foi escrita; `null` = o padrão. */
  readonly regraPagamentoTexto: string | null;
  /** O que o sistema entendeu dela, numa frase. */
  readonly regraPagamentoDescricao: string | null;
}

/** Um contrato na lista da operação: com cliente e fornecedor pelo nome. */
export interface ContratoDaOperacao extends ContratoPassivo {
  readonly clienteNome: string;
  readonly clienteCnpj: string;
  readonly fornecedorNome: string;
  readonly criadoEm: string;
}

interface PainelDeParcelas {
  readonly contratosAtivos: number;
  readonly atrasadas: number;
  readonly aVencer: number;
  readonly aguardandoConfirmacao: number;
}

/**
 * ICMS Passivo — os contratos de renegociação da operação.
 *
 * ## Abre em "Todos"
 *
 * A tela lista todos os contratos (`GET /passivo/contratos`), com cliente e
 * fornecedor pelo nome. O filtro de cliente oferece só quem tem contrato e
 * filtra na própria lista — trocar o filtro não vai ao servidor. Os
 * indicadores acompanham: da operação inteira em "Todos", do cliente quando
 * filtrado. "Novo contrato" escolhe o cliente dentro do modal.
 *
 * ## Simulação vira contrato, contrato não volta a ser simulação
 *
 * `SIMULACAO` é rascunho: editável, sem parcelas geradas. Ativar materializa o
 * cronograma. É por isso que "Editar" some depois de ativo — a API recusa
 * (RN-220), e a tela não deve oferecer o que vai ser recusado.
 *
 * ## O endereço escolhe o cliente e abre o contrato
 *
 * `?cliente=<businessId>&contrato=<id>` é como "Fechar negociação" (tela de
 * simulação) chega aqui: com o cliente já escolhido e o contrato recém-criado
 * aberto. O contrato aberto é guardado por **id** e resolvido contra a lista a
 * cada render — assim, quando ativar/finalizar refaz a lista, o modal mostra o
 * status novo em vez de uma cópia velha do objeto.
 */
/** Os contratos, com o status por extenso e os indicadores de andamento. */
const COLUNAS_DA_PLANILHA: readonly ColunaDaPlanilha<ContratoDaOperacao>[] = [
  { titulo: 'Cliente', largura: 44, valor: (c) => c.clienteNome },
  { titulo: 'CNPJ', largura: 20, valor: (c) => formatarCnpj(c.clienteCnpj) },
  { titulo: 'Fornecedor', largura: 36, valor: (c) => c.fornecedorNome },
  { titulo: 'Status', largura: 14, valor: (c) => ROTULO_STATUS_PASSIVO[c.status] },
  { titulo: 'Dívida original', formato: 'moeda', valor: (c) => c.valorDividaOriginal },
  { titulo: 'Valor a pagar', formato: 'moeda', valor: (c) => c.valorFinalPagar },
  { titulo: 'Economia', formato: 'moeda', valor: (c) => c.economia },
  { titulo: 'Economia (%)', formato: 'percentual', valor: (c) => c.percentualEconomia },
  { titulo: 'Parcelas', largura: 10, valor: (c) => c.quantidadeParcelas },
  { titulo: 'Cronograma gerado', largura: 12, valor: (c) => simOuNao(c.cronogramaGerado) },
  { titulo: 'Checklist iniciado', largura: 12, valor: (c) => simOuNao(c.checklistIniciado) },
];

/**
 * O que o assessor simulou, como ponto de partida do contrato: a dívida, o
 * crédito que a simulação calculou, o deságio e o prazo. O fornecedor e o dia
 * de vencimento ficam para a operação.
 */
function inicioDoPedido(p: PedidoDeFechamento): Partial<FormDoContrato> {
  const entradas = (p.simulacao?.['entradas'] ?? {}) as Record<string, unknown>;
  const resultado = (p.simulacao?.['resultado'] ?? {}) as Record<string, unknown>;
  const numero = (v: unknown): number | undefined =>
    typeof v === 'number' && Number.isFinite(v) ? v : undefined;
  const texto = (v: number | undefined): string | undefined =>
    v === undefined ? undefined : String(v).replace('.', ',');
  const inicio: Partial<Record<keyof FormDoContrato, string | undefined>> = {
    valorDividaOriginal: moedaParaCampo(numero(entradas['valorDivida'])) || undefined,
    valorCreditoComprado: moedaParaCampo(numero(resultado['valorCreditoComprado'])) || undefined,
    percentualDesagio: texto(numero(entradas['percentualDesagio'])),
    quantidadeParcelas: texto(numero(entradas['quantidadeParcelas'])),
    taxaJurosMensal: texto(numero(entradas['taxaJurosMensal'])),
    indice:
      entradas['indice'] === 'price' || entradas['indice'] === 'sac'
        ? entradas['indice']
        : undefined,
  };
  return Object.fromEntries(
    Object.entries(inicio).filter(([, v]) => v !== undefined),
  ) as Partial<FormDoContrato>;
}

export function PassivoContratos(): JSX.Element {
  const [parametros, setParametros] = useSearchParams();
  /** O filtro: `''` é "Todos". */
  const [clienteId, setClienteId] = useState(parametros.get('cliente') ?? '');
  const [criando, setCriando] = useState(false);
  /** O pedido do assessor que está virando contrato, se for o caso. */
  const [pedidoEmMontagem, setPedidoEmMontagem] = useState<PedidoDeFechamento | null>(null);
  // Memorizado: o modal reaplica o início quando ele muda.
  const inicio = useMemo(
    () => (pedidoEmMontagem === null ? null : inicioDoPedido(pedidoEmMontagem)),
    [pedidoEmMontagem],
  );
  const [abrindoId, setAbrindoId] = useState<string | null>(parametros.get('contrato'));
  const dialogo = useDialogo();
  const queryClientDaLista = useQueryClient();

  // Item 7 de 15/09 — RN-219: só a simulação se exclui; contrato ativo se cancela.
  const excluir = useMutation({
    mutationFn: (contratoId: string) =>
      requisicaoAutenticada<void>(`/passivo/contratos/${contratoId}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      void queryClientDaLista.invalidateQueries({ queryKey: ['passivo-contratos'] });
      void queryClientDaLista.invalidateQueries({ queryKey: ['passivo-painel'] });
      void queryClientDaLista.invalidateQueries({ queryKey: ['passivo-simulacoes'] });
    },
  });
  // A mesma exclusão, para as simulações marcadas.
  const excluirMarcados = useAcaoEmLote<ContratoDaOperacao>({
    acao: (c) => requisicaoAutenticada<void>(`/passivo/contratos/${c.id}`, { metodo: 'DELETE' }),
    nome: (c) => c.clienteNome,
    aoTerminar: () => {
      void queryClientDaLista.invalidateQueries({ queryKey: ['passivo-contratos'] });
      void queryClientDaLista.invalidateQueries({ queryKey: ['passivo-painel'] });
      void queryClientDaLista.invalidateQueries({ queryKey: ['passivo-simulacoes'] });
    },
  });

  /** Criar o contrato de um pedido é atendê-lo: o assessor passa a vê-lo na lista dele. */
  const atenderAoMontar = useMutation({
    mutationFn: atenderPedido,
    onSettled: () => invalidarPedidos(queryClientDaLista),
  });

  const filtrarCliente = (id: string): void => {
    setClienteId(id);
    // O endereço acompanha o filtro, para o link copiado levar ao mesmo lugar.
    setParametros(id === '' ? {} : { cliente: id }, { replace: true });
  };

  const contratos = useQuery({
    queryKey: ['passivo-contratos', 'todos'],
    queryFn: () => requisicaoAutenticada<ContratoDaOperacao[]>('/passivo/contratos'),
  });

  const painel = useQuery({
    queryKey: ['passivo-painel', clienteId === '' ? 'todos' : clienteId],
    queryFn: () =>
      requisicaoAutenticada<PainelDeParcelas>(
        clienteId === ''
          ? '/passivo/painel-parcelas'
          : `/passivo/clientes/${clienteId}/painel-parcelas`,
      ),
  });

  const todos = contratos.data ?? [];
  const lista = clienteId === '' ? todos : todos.filter((c) => c.clienteId === clienteId);

  /** Só quem tem contrato entra no filtro, em ordem alfabética. */
  const opcoesDeCliente = useMemo(() => {
    const nomes = new Map<string, string>();
    for (const c of contratos.data ?? []) nomes.set(c.clienteId, c.clienteNome);
    // Um cliente vindo pelo endereço, ainda sem contrato, não some do filtro.
    if (clienteId !== '' && !nomes.has(clienteId)) nomes.set(clienteId, 'Cliente sem contratos');
    return [
      { valor: '', rotulo: 'Todos' },
      ...[...nomes.entries()]
        .sort(([, a], [, b]) => a.localeCompare(b, 'pt-BR'))
        .map(([valor, rotulo]) => ({ valor, rotulo })),
    ];
  }, [contratos.data, clienteId]);

  const nomeDoFiltro = opcoesDeCliente.find((o) => o.valor === clienteId)?.rotulo ?? '';

  const colunas: readonly ColunaTabela<ContratoDaOperacao>[] = [
    {
      chave: 'clienteNome',
      titulo: 'Cliente',
      larguraMinima: '16rem',
      celula: (c) => (
        <span className="flex flex-col">
          <Link
            to={`/clientes/${c.clienteId}`}
            className="text-sm text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
          >
            {c.clienteNome}
          </Link>
          <span className="valor-fiscal text-xs text-conteudo-sutil">
            {formatarCnpj(c.clienteCnpj)}
          </span>
        </span>
      ),
    },
    {
      chave: 'fornecedorNome',
      titulo: 'Fornecedor',
      larguraMinima: '12rem',
      celula: (c) => <span className="text-sm text-conteudo-secundario">{c.fornecedorNome}</span>,
    },
    {
      chave: 'status',
      titulo: 'Status',
      larguraMinima: '9rem',
      celula: (c) => <Selo tom={tomDoStatus(c.status)}>{rotuloDoStatus(c.status)}</Selo>,
    },
    {
      chave: 'valorDividaOriginal',
      titulo: 'Dívida original',
      numerica: true,
      celula: (c) => <ValorFiscal valor={c.valorDividaOriginal} />,
    },
    {
      chave: 'valorFinalPagar',
      titulo: 'Valor a pagar',
      numerica: true,
      celula: (c) => <ValorFiscal valor={c.valorFinalPagar} />,
    },
    {
      chave: 'economia',
      titulo: 'Economia',
      numerica: true,
      celula: (c) => (
        <span className="flex flex-col items-end">
          <ValorFiscal valor={c.economia} variante="economia" />
          <ValorPercentual percentual={c.percentualEconomia} tamanho="pequeno" />
        </span>
      ),
    },
    {
      chave: 'quantidadeParcelas',
      titulo: 'Parcelas',
      numerica: true,
      larguraMinima: '7rem',
      celula: (c) => (
        <span className="valor-fiscal text-sm">
          {c.quantidadeParcelas}
          {!c.cronogramaGerado && (
            <span className="ml-1 text-xs text-conteudo-sutil">(não gerado)</span>
          )}
        </span>
      ),
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      alinhamento: 'direita',
      larguraMinima: '8rem',
      celula: (c) => (
        <span className="flex items-center justify-end gap-2">
          <Botao variante="secundario" tamanho="pequeno" onClick={() => setAbrindoId(c.id)}>
            Abrir
          </Botao>
          <MenuDeAcoes
            rotulo={`contrato ${rotuloDoStatus(c.status).toLowerCase()}`}
            itens={[
              { rotulo: 'Abrir', aoEscolher: () => setAbrindoId(c.id) },
              {
                rotulo: 'Excluir',
                perigo: true,
                desabilitado: c.status !== 'SIMULACAO',
                descricao:
                  c.status === 'SIMULACAO'
                    ? 'Apaga o rascunho.'
                    : 'Só em simulação — contrato ativo se cancela por dentro.',
                aoEscolher: () => {
                  void (async () => {
                    const ok = await dialogo.confirmar({
                      titulo: 'Excluir esta simulação?',
                      descricao: 'O rascunho some da lista. Nenhuma parcela foi gerada.',
                      rotuloConfirmar: 'Excluir',
                      tom: 'perigo',
                    });
                    if (ok) excluir.mutate(c.id);
                  })();
                },
              },
            ]}
          />
        </span>
      ),
    },
  ];

  // O contrato aberto vem da lista inteira: o filtro não fecha o modal.
  const abrindo = abrindoId === null ? null : (todos.find((c) => c.id === abrindoId) ?? null);

  return (
    <Pagina
      titulo="Contratos de ICMS Passivo"
      descricao="Renegociação de dívida com crédito comprado. Todos os contratos da operação; filtre por cliente se quiser."
      {...(contratos.data === undefined ? {} : { contagem: lista.length })}
      acao={
        <div className="flex flex-wrap items-end gap-3">
          <CampoDeSelecao
            rotulo="Cliente"
            className="w-72"
            value={clienteId}
            onChange={(e) => filtrarCliente(e.target.value)}
            opcoes={opcoesDeCliente}
          />
          <BotaoExtrairXlsx
            titulo={clienteId === '' ? 'Contratos Passivo' : `Contratos Passivo - ${nomeDoFiltro}`}
            disabled={contratos.data === undefined}
            colunas={COLUNAS_DA_PLANILHA}
            linhas={lista}
          />
          <Botao onClick={() => setCriando(true)}>Novo contrato</Botao>
        </div>
      }
    >
      <PedidosDaOperacao
        servico="PASSIVO"
        rotuloAbrir={(p) => (p.contratoPassivoId === null ? 'Montar contrato' : 'Abrir contrato')}
        aoAbrir={(p) => {
          if (p.contratoPassivoId !== null) {
            setAbrindoId(p.contratoPassivoId);
            return;
          }
          setPedidoEmMontagem(p);
          setCriando(true);
        }}
      />
      {atenderAoMontar.isError && (
        <ErroDaApi
          titulo="O contrato foi criado, mas o pedido continua aberto — marque como atendido"
          erro={atenderAoMontar.error}
        />
      )}

      {painel.data !== undefined && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <CartaoIndicador rotulo="Contratos ativos" valor={painel.data.contratosAtivos} />
          <CartaoIndicador
            rotulo="Parcelas atrasadas"
            valor={painel.data.atrasadas}
            {...(painel.data.atrasadas > 0
              ? { detalhe: 'Cobrança em aberto', variante: 'contorno' as const }
              : {})}
          />
          <CartaoIndicador rotulo="A vencer" valor={painel.data.aVencer} />
          <CartaoIndicador
            rotulo="Aguardando confirmação"
            valor={painel.data.aguardandoConfirmacao}
            {...(painel.data.aguardandoConfirmacao > 0
              ? { detalhe: 'O cliente pagou; falta conferir' }
              : {})}
          />
        </div>
      )}

      {contratos.isError && <ErroDaApi titulo="Não foi possível carregar" erro={contratos.error} />}

      {!contratos.isPending && lista.length === 0 ? (
        <EstadoVazio
          titulo={
            clienteId === '' ? 'Nenhum contrato de Passivo ainda' : 'Nenhum contrato deste cliente'
          }
          descricao="Simule em Simulações (Créditos ICMS Passivos) e use “Fechar negociação” por lá, ou crie o contrato direto."
          acao={
            <span className="inline-flex flex-wrap items-center justify-center gap-3">
              <Botao onClick={() => setCriando(true)}>Novo contrato</Botao>
              {/* O item de menu se chama "Simulações"; o link leva direto. */}
              <Link
                to="/passivo"
                className="text-sm text-acento-ciano underline-offset-4 hover:underline"
              >
                Ir para Simulações
              </Link>
            </span>
          }
        />
      ) : (
        <>
          {excluir.isError && <ErroDaApi titulo="Não foi possível excluir" erro={excluir.error} />}
          <AvisoDoLote
            desfecho={excluirMarcados.desfecho}
            feitos={(n) => (n === 1 ? '1 simulação excluída' : `${n} simulações excluídas`)}
            aoFechar={excluirMarcados.limparDesfecho}
          />
          <Tabela
            legenda="Contratos de ICMS Passivo"
            colunas={colunas}
            dados={lista}
            chaveDaLinha={(c) => c.id}
            chaveDeCabecalhoDaLinha="clienteNome"
            carregando={contratos.isPending}
            selecao={{
              substantivo: { singular: 'contrato', plural: 'contratos' },
              nomeDaLinha: (c) => c.clienteNome,
              // RN-219: só a simulação se exclui, como no "Excluir" da linha.
              bloqueio: (c) =>
                c.status === 'SIMULACAO'
                  ? null
                  : 'Só em simulação — contrato ativo se cancela por dentro.',
              acoes: (marcados, limpar) => (
                <BotaoDoLote
                  rotulo="Excluir"
                  perigo
                  marcadas={marcados}
                  limpar={limpar}
                  lote={excluirMarcados}
                  confirmacao={(m) => ({
                    titulo:
                      m.length === 1
                        ? 'Excluir esta simulação?'
                        : `Excluir ${m.length} simulações?`,
                    descricao: `${resumirNomes(m.map((c) => c.clienteNome))}.\n\nOs rascunhos somem da lista. Nenhuma parcela foi gerada.`,
                    rotuloConfirmar: 'Excluir',
                    tom: 'perigo',
                  })}
                />
              ),
            }}
          />
        </>
      )}

      <ModalDeCriacao
        clienteInicial={pedidoEmMontagem?.clienteId ?? clienteId}
        aberto={criando}
        inicio={inicio}
        aoFechar={() => {
          setCriando(false);
          setPedidoEmMontagem(null);
        }}
        aoCriar={(contrato) => {
          if (pedidoEmMontagem !== null) atenderAoMontar.mutate(pedidoEmMontagem.id);
          setPedidoEmMontagem(null);
          setAbrindoId(contrato.id);
        }}
      />

      <ParcelasDoContrato contrato={abrindo} aoFechar={() => setAbrindoId(null)} />
    </Pagina>
  );
}

/* ─── Criação ─────────────────────────────────────────────────────────────── */

const FORM_VAZIO = {
  fornecedorId: '',
  valorDividaOriginal: '',
  valorCreditoComprado: '',
  percentualDesagio: '25',
  quantidadeParcelas: '12',
  taxaJurosMensal: '1',
  diaVencimento: '10',
  indice: 'sac',
  regraPagamentoTexto: '',
};
type FormDoContrato = typeof FORM_VAZIO;

function ModalDeCriacao(props: {
  /** O cliente do filtro ou do pedido; `''` = escolher no modal. */
  readonly clienteInicial: string;
  readonly aberto: boolean;
  readonly aoFechar: () => void;
  /** Aberto a partir do pedido de um assessor: os números que ele simulou. */
  readonly inicio?: Partial<FormDoContrato> | null;
  readonly aoCriar?: (contrato: ContratoPassivo) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const clientes = useClientesParaEscolher();
  const fornecedores = useCarteira('fornecedores');

  const [form, setForm] = useState(FORM_VAZIO);
  const [clienteId, setClienteId] = useState(props.clienteInicial);
  const idDaExplicacao = useId();
  const leitura = useLeituraDaFormaDePagamento(
    form.regraPagamentoTexto,
    Number(form.quantidadeParcelas) || 1,
  );

  // Cada abertura começa no cliente do filtro (ou do pedido).
  const { aberto, clienteInicial } = props;
  useEffect(() => {
    if (aberto) setClienteId(clienteInicial);
  }, [aberto, clienteInicial]);

  const inicio = props.inicio;
  useEffect(() => {
    if (props.aberto && inicio !== undefined && inicio !== null) {
      setForm((f) => ({ ...f, ...inicio }));
    }
  }, [props.aberto, inicio]);

  const definir = (campo: keyof typeof form, valor: string): void =>
    setForm((f) => ({ ...f, [campo]: valor }));

  const criar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<ContratoPassivo>('/passivo/contratos', {
        metodo: 'POST',
        corpo: {
          clienteId,
          fornecedorId: form.fornecedorId,
          valorDividaOriginal: lerValorDigitado(form.valorDividaOriginal) ?? 0,
          valorCreditoComprado: lerValorDigitado(form.valorCreditoComprado) ?? 0,
          percentualDesagio: Number(form.percentualDesagio),
          quantidadeParcelas: Number(form.quantidadeParcelas),
          taxaJurosMensal: Number(form.taxaJurosMensal),
          diaVencimento: Number(form.diaVencimento),
          indice: form.indice,
          ...(form.regraPagamentoTexto.trim() === ''
            ? {}
            : { regraPagamentoTexto: form.regraPagamentoTexto.trim() }),
        },
      }),
    onSuccess: (contrato) => {
      void queryClient.invalidateQueries({ queryKey: ['passivo-contratos'] });
      void queryClient.invalidateQueries({ queryKey: ['passivo-painel'] });
      void queryClient.invalidateQueries({ queryKey: ['passivo-simulacoes'] });
      fechar();
      props.aoCriar?.(contrato);
    },
  });

  const fechar = (): void => {
    criar.reset();
    props.aoFechar();
  };

  const completo =
    clienteId !== '' &&
    form.fornecedorId !== '' &&
    lerValorDigitado(form.valorDividaOriginal) !== null &&
    lerValorDigitado(form.valorCreditoComprado) !== null &&
    formaDePagamentoValida(form.regraPagamentoTexto, leitura);

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={fechar}
      titulo="Novo contrato de Passivo"
      largura="larga"
      rodape={
        <>
          <Botao variante="secundario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => criar.mutate()} disabled={!completo} carregando={criar.isPending}>
            Criar como simulação
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Aviso tom="informacao" titulo="Nasce como simulação">
          O contrato é criado em <strong>SIMULAÇÃO</strong>: editável, sem parcelas geradas. Ativar
          materializa o cronograma e trava a edição.
        </Aviso>

        <CampoDeSelecao
          rotulo="Cliente"
          value={clienteId}
          onChange={(e) => setClienteId(e.target.value)}
          opcoes={[
            { valor: '', rotulo: 'Selecione…' },
            ...(clientes.data ?? []).map((c) => ({
              valor: c.businessId,
              rotulo: c.razaoSocial,
            })),
          ]}
        />

        <CampoDeSelecao
          rotulo="Fornecedor do crédito"
          value={form.fornecedorId}
          onChange={(e) => definir('fornecedorId', e.target.value)}
          opcoes={[
            { valor: '', rotulo: 'Selecione…' },
            ...(fornecedores.data ?? []).map((f) => ({
              valor: f.businessId,
              rotulo: f.razaoSocial,
            })),
          ]}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <CampoDeMoeda
            rotulo="Dívida original"
            ajuda="O débito de ICMS que está sendo renegociado."
            value={form.valorDividaOriginal}
            onChange={(texto) => definir('valorDividaOriginal', texto)}
          />
          <CampoDeMoeda
            rotulo="Crédito comprado"
            ajuda="O crédito que vai abater a dívida."
            value={form.valorCreditoComprado}
            onChange={(texto) => definir('valorCreditoComprado', texto)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Deságio (%)"
            inputMode="decimal"
            value={form.percentualDesagio}
            onChange={(e) => definir('percentualDesagio', e.target.value)}
          />
          <Campo
            rotulo="Juros mensal (%)"
            inputMode="decimal"
            value={form.taxaJurosMensal}
            onChange={(e) => definir('taxaJurosMensal', e.target.value)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Campo
            rotulo="Parcelas"
            type="number"
            min={1}
            value={form.quantidadeParcelas}
            onChange={(e) => definir('quantidadeParcelas', e.target.value)}
          />
          <Campo
            rotulo="Dia do vencimento"
            type="number"
            min={1}
            max={28}
            value={form.diaVencimento}
            onChange={(e) => definir('diaVencimento', e.target.value)}
          />
          <CampoDeSelecao
            rotulo="Sistema"
            value={form.indice}
            onChange={(e) => definir('indice', e.target.value)}
            opcoes={[
              { valor: 'sac', rotulo: 'SAC' },
              { valor: 'price', rotulo: 'Price' },
            ]}
            // A explicação fica embaixo da linha inteira: na coluna do campo,
            // um terço da largura, ela virava cinco linhas espremidas.
            aria-describedby={idDaExplicacao}
          />
        </div>
        <p id={idDaExplicacao} className="-mt-2 text-xs text-conteudo-secundario">
          {explicacaoDoSistema(form.indice === 'price' ? 'price' : 'sac', form.taxaJurosMensal)}
        </p>

        <CampoDaFormaDePagamento
          valor={form.regraPagamentoTexto}
          aoMudar={(texto) => definir('regraPagamentoTexto', texto)}
          leitura={leitura}
        />

        {criar.isError && <ErroDaApi titulo="Não foi possível criar" erro={criar.error} />}
      </div>
    </Modal>
  );
}

/* ─── Auxiliares ──────────────────────────────────────────────────────────── */

export function tomDoStatus(status: ContratoPassivo['status']): TomSemantico {
  if (status === 'ATIVO') return 'progresso';
  if (status === 'FINALIZADO') return 'positivo';
  if (status === 'CANCELADO') return 'critico';
  return 'neutro';
}

/**
 * Rótulo de cada status do contrato de Passivo.
 *
 * O enum não tem acento, e trocar `_` por espaço e capitalizar devolvia
 * "Simulacao" na tela — texto de sistema exposto. O mapa é a fonte; se um
 * status novo entrar no enum, o tipo obriga a dar rótulo a ele.
 */
export const ROTULO_STATUS_PASSIVO: Readonly<Record<ContratoPassivo['status'], string>> = {
  SIMULACAO: 'Simulação',
  ATIVO: 'Ativo',
  FINALIZADO: 'Finalizado',
  CANCELADO: 'Cancelado',
};

export function rotuloDoStatus(status: ContratoPassivo['status']): string {
  return ROTULO_STATUS_PASSIVO[status];
}

/**
 * Para as chaves que não têm rótulo próprio (status de parcela, por exemplo):
 * `_` vira espaço e a primeira letra sobe. Um status de contrato que passar por
 * aqui sai com o rótulo acentuado do mapa, não a forma crua.
 */
export function humanizar(chave: string): string {
  if (chave in ROTULO_STATUS_PASSIVO) {
    return ROTULO_STATUS_PASSIVO[chave as ContratoPassivo['status']];
  }
  const t = chave.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}
